import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import {
  Editor,
  EditorProvider,
  Toolbar,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  BtnBulletList,
  BtnNumberedList,
  BtnUndo,
  BtnRedo,
  useEditorState
} from "react-simple-wysiwyg"
import styles from "./RichTextEditor.module.css"

// Each row is [x, width] within a 16-wide canvas. Long rows span full width;
// short rows shift to the left/center/right to convey the alignment.
const FULL = [0, 16]
const LEFT = [0, 10]
const CENTER = [3, 10]
const RIGHT = [6, 10]

const ALIGN_OPTIONS = [
  { command: "justifyLeft", label: "Align left", rows: [FULL, LEFT, FULL, LEFT] },
  { command: "justifyCenter", label: "Align center", rows: [FULL, CENTER, FULL, CENTER] },
  { command: "justifyRight", label: "Align right", rows: [FULL, RIGHT, FULL, RIGHT] },
  { command: "justifyFull", label: "Justify", rows: [FULL, FULL, FULL, FULL] }
]

const AlignIcon = ({ rows }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    {[2, 6, 10, 14].map((y, index) => (
      <rect key={y} x={rows[index][0]} y={y - 0.8} width={rows[index][1]} height="1.6" rx="0.8" fill="currentColor" />
    ))}
  </svg>
)

AlignIcon.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired
}

// A single split-button (Google Docs style) that opens a popover with the four
// alignment options, applying the command the same way the built-in buttons do.
const AlignDropdown = () => {
  const { $el, $selection } = useEditorState()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  const active = ALIGN_OPTIONS.find((option) => !!$selection && window.document.queryCommandState(option.command)) || ALIGN_OPTIONS[0]

  useEffect(() => {
    if (!open) return undefined
    const handleClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false)
    }
    window.document.addEventListener("mousedown", handleClickOutside)
    return () => window.document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  const applyAlign = (event, command) => {
    event.preventDefault()
    if (window.document.activeElement !== $el) $el?.focus()
    window.document.execCommand(command)
    setOpen(false)
  }

  return (
    <div className={styles.alignWrap} ref={wrapRef}>
      <button
        type="button"
        className={`rsw-btn ${styles.alignTrigger}`}
        title={active.label}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen((prev) => !prev)}
      >
        <AlignIcon rows={active.rows} />
        <span className={styles.alignCaret}>▾</span>
      </button>
      {open && (
        <div className={styles.alignMenu}>
          {ALIGN_OPTIONS.map((option) => (
            <button
              key={option.command}
              type="button"
              title={option.label}
              className={`${styles.alignMenuItem} ${option.command === active.command ? styles.alignMenuItemActive : ""}`}
              onMouseDown={(event) => applyAlign(event, option.command)}
            >
              <AlignIcon rows={option.rows} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const RichTextEditor = ({ id, name, value, onChange, placeholder, className }) => (
  <EditorProvider>
    <Editor
      id={id}
      name={name}
      placeholder={placeholder}
      className={`${styles.editor} ${className}`.trim()}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
    >
      <Toolbar>
        <BtnUndo />
        <BtnRedo />
        <BtnBold />
        <BtnItalic />
        <BtnUnderline />
        <AlignDropdown />
        <BtnBulletList />
        <BtnNumberedList />
      </Toolbar>
    </Editor>
  </EditorProvider>
)

RichTextEditor.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string
}

RichTextEditor.defaultProps = {
  className: ""
}

// Existing records may hold plain text with line breaks. Convert those to <br>
// once on load so they render in the editor; leave already-HTML content alone
// to avoid double-converting the editor's own output.
export const toEditorHtml = (value = "") => (/<[a-z][\s\S]*>/i.test(value) ? value : value.replaceAll("\n", "<br>"))

export default RichTextEditor
